import { createContext, ReactNode, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useList } from "@refinedev/core";
import { useProjectNavigate } from "pages/media/projects/hooks/useProjectNavigate";
import { ContactResponse, Storyboard } from "pages/media/types/";
import { useProjectAssets } from "../pages/media/components/storyboardEditor/hooks/useProjectAssets";
import { useBrandKit } from "hooks/useBrandKit";
import { useMediaAssetLibraries } from "pages/asset-libraries/useMediaAssetLibraries/";
import { useMediaAssets } from "pages/asset-libraries/useMediaAssets/";
import { useMediaGenerationSettings } from "pages/media/components/storyboardEditor/hooks/useMediaGenerationSettings/";
import { useLoadAllAssets } from "hooks/useLoadAllAssets/";
import { useTemplates } from "pages/media/components/storyboardEditor/hooks/useTemplates/";
import {
  ThemeResponse,
  useOrgThemes,
  usePublicThemes,
} from "pages/media/components/themeEditor/hooks/useThemeApi";
import {
  TemplateEditorProps,
  TemplateEditorStore,
  createTemplateEditorStore,
} from "pages/media/components/storyboardEditor/store/useTemplateEditorStore/";
import { loadImageRatio } from "pages/media/components/storyboardEditor/helpers/";
import { usePresenterRatios } from "pages/media/components/storyboardEditor/hooks/usePresenterRatios";
import {
  PreviewSizes,
  usePreviewResize,
} from "pages/media/components/storyboardEditor/hooks/usePreviewResize/";
import {
  SectionWithSlides,
  useStoryboardWithSlides,
} from "hooks/useStoryboardWithSlides/";
import { stripHighlightAnchors } from "pages/media/helpers/scriptAnchors";
import { useTemplatesStatusStore } from "pages/media/components/storyboardEditor/store/useTemplatesStatusStore";
import { TabKeys } from "pages/media/components/storyboardEditor/types";

type TemplatesData = ReturnType<typeof useProjectAssets>;
type MediaGenerationSettingsData = ReturnType<
  typeof useMediaGenerationSettings
>;
type AllLoadedAssetsData = ReturnType<typeof useLoadAllAssets>;
type BrandKitData = ReturnType<typeof useBrandKit>;
type AssetLibrariesData = ReturnType<typeof useMediaAssetLibraries>;
type MediaAssetsData = ReturnType<typeof useMediaAssets>;
type StoryboardWithSlidesData = ReturnType<typeof useStoryboardWithSlides>;

export type TemplateDataContextType = {
  projectId: string | undefined;
  instanceId: string | undefined;
  organizationId: string | undefined;
  templatesData: TemplatesData;
  storyboardWithSlidesData: StoryboardWithSlidesData;
  mediaGenerationSettingsData: MediaGenerationSettingsData;
  assetLibrariesData: AssetLibrariesData;
  brandKitData: BrandKitData;
  storyboardSections: SectionWithSlides[];
  storyboard: Storyboard | undefined;
  projectTheme: ThemeResponse | undefined;
  updateSection: (
    sectionId: string,
    updates: Partial<Storyboard["sections"][number]>
  ) => void;
  isLoading: boolean;
  mediaAssetsData: MediaAssetsData;
  projectMediaAssetsData: AllLoadedAssetsData;
  previewData: {
    previewSizes: PreviewSizes | undefined;
  };
  placeholderAssets?: Record<string, string>;
  logoRatio: number | undefined;
  presenterRatio: number | undefined;
  // Optional so hand-built contexts (theme editor, brand kit preview) don't need it.
  presenterRatiosById?: Map<string, number>;
  contacts: ContactResponse[] | undefined;
};

export const TemplateDataContext =
  createContext<TemplateDataContextType | null>(null);

export const TemplateEditorStoreContext = createContext<
  TemplateEditorStore | undefined
>(undefined);

const storeRegistry = new Map<
  string,
  ReturnType<typeof createTemplateEditorStore>
>();

const getOrCreateStore = (
  projectId: string | undefined,
  initialProps: TemplateEditorProps | undefined
) => {
  if (!projectId) return;
  let store = storeRegistry.get(projectId);
  if (!store && initialProps) {
    store = createTemplateEditorStore(initialProps);
    storeRegistry.set(projectId, store);
  }
  return store;
};

export const TemplateDataProvider = ({ children }: { children: ReactNode }) => {
  const {
    params: { projectId, instanceId },
  } = useProjectNavigate();

  const templatesData = useProjectAssets({ projectId, instanceId });

  const mediaGenerationSettingsData = useMediaGenerationSettings({
    projectId,
    organizationId: templatesData.organizationId,
    instanceId,
  });

  const storyboardWithSlidesData = useStoryboardWithSlides(
    templatesData.storyboardPath,
    templatesData.slidesPath
  );

  const {
    sections: rawStoryboardSections,
    slides,
    updateSection,
    storyboard,
  } = storyboardWithSlidesData;

  // Highlight tags drive the document highlighter, which is hidden for prompt
  // projects — strip them so the script editor doesn't expose dead markup.
  const storyboardSections = useMemo(() => {
    if (!templatesData.isPromptProject) return rawStoryboardSections;
    return rawStoryboardSections.map((section) => ({
      ...section,
      script: {
        ...section.script,
        content: stripHighlightAnchors(section.script.content),
      },
    }));
  }, [rawStoryboardSections, templatesData.isPromptProject]);

  const brandKitData = useBrandKit({
    brandKitId: templatesData.brandKitId,
    organizationId: templatesData.organizationId,
  });

  const { data: contactsData, isLoading: isContactsLoading } =
    useList<ContactResponse>({
      resource: `media/${templatesData.organizationId}/contacts`,
      queryOptions: { enabled: !!templatesData.organizationId },
    });
  const contacts = contactsData?.data;

  // Load the aspect ratio of every presenter used by the project (project-level
  // + per-chapter overrides), keyed by id, so each chapter's avatar is
  // sized/positioned against its own image.
  const {
    ratiosById: presenterRatiosById,
    isLoading: isPresenterRatiosLoading,
  } = usePresenterRatios({
    projectPresenter: templatesData.presenter,
    projectPresenters: templatesData.projectPresenters,
    chapters: mediaGenerationSettingsData.mediaGenerationSettings?.chapters,
  });

  // Project-level ratio (used for default templates and intro/closing).
  const presenterRatio = templatesData.presenter?.id
    ? presenterRatiosById.get(templatesData.presenter.id)
    : undefined;
  const { data: logoRatio } = useQuery<number>({
    queryKey: ["imageRatio", "logo", brandKitData.logo?.path],
    queryFn: () => loadImageRatio(brandKitData.logo!.path),
    enabled: !!brandKitData.logo?.path && !brandKitData.isBrandKitLoading,
  });

  const assetLibrariesData = useMediaAssetLibraries({
    projectId,
    organizationId: templatesData.organizationId,
  });

  const mediaAssetsData = useMediaAssets(
    assetLibrariesData.organizationAssetLibraries[0]?.id,
    templatesData.organizationId
  );

  const projectMediaAssetsData = useLoadAllAssets({
    currentAssetLibraryId: assetLibrariesData.projectAssetLibraryId ?? "",
  });

  const { previewSizes } = usePreviewResize(
    mediaGenerationSettingsData.mediaAspectRatio
  );

  const { data: orgThemes } = useOrgThemes(templatesData.organizationId);
  const { data: publicThemes } = usePublicThemes();
  const projectTheme = useMemo(() => {
    if (!templatesData.themeId) return undefined;
    return (
      orgThemes?.items.find((t) => t.id === templatesData.themeId) ??
      publicThemes?.items.find((t) => t.id === templatesData.themeId) ??
      publicThemes?.items[0]
    );
  }, [templatesData.themeId, orgThemes, publicThemes]);

  const initialTemplateEditorProps = useTemplates(
    mediaGenerationSettingsData,
    slides,
    templatesData.organizationId,
    templatesData.isFlashProject,
    projectId ? storeRegistry.get(projectId) : undefined,
    storyboardSections,
    templatesData.hasTransition,
    {
      brandKitData,
      initialPresenter: templatesData.presenter,
      projectPresenters: templatesData.projectPresenters,
      presenterRatiosById,
      contact: templatesData.contact,
      contacts,
      presenterRatio,
      logoRatio,
      // Also wait for the presenter image-ratio queries: mapPresenter discards
      // the presenter when its ratio is undefined, and the store is only
      // created once, so mapping before the ratios resolve permanently drops
      // a chapter's presenter on reload.
      isPresenterLoading:
        templatesData.isDataLoading ||
        isPresenterRatiosLoading ||
        isContactsLoading,
    }
  );

  const store = getOrCreateStore(projectId, initialTemplateEditorProps);

  useEffect(() => {
    if (store) {
      // Discard a stale dirty-flag left over from an earlier, unrelated editor session in this tab
      useTemplatesStatusStore.getState().setActiveTab(TabKeys.script);
      useTemplatesStatusStore.getState().resetTemplateUpdates();
      useTemplatesStatusStore.getState().resetBulkTemplateUpdates();
    }
  }, [store]);

  useEffect(() => {
    return () => {
      if (projectId) {
        storeRegistry.delete(projectId);
      }
    };
  }, [projectId]);

  const contextValue = useMemo(
    () => ({
      projectId,
      instanceId,
      organizationId: templatesData.organizationId,
      templatesData,
      mediaGenerationSettingsData,
      brandKitData,
      storyboardSections,
      storyboard,
      projectTheme,
      updateSection,
      isLoading: store === undefined,
      assetLibrariesData,
      mediaAssetsData,
      projectMediaAssetsData,
      previewData: {
        previewSizes,
      },
      storyboardWithSlidesData,
      logoRatio,
      presenterRatio,
      presenterRatiosById,
      contacts,
    }),
    [
      assetLibrariesData,
      brandKitData,
      instanceId,
      mediaAssetsData,
      mediaGenerationSettingsData,
      previewSizes,
      projectId,
      projectMediaAssetsData,
      store,
      storyboardSections,
      templatesData,
      updateSection,
      storyboard,
      projectTheme,
      storyboardWithSlidesData,
      logoRatio,
      presenterRatio,
      presenterRatiosById,
      contacts,
    ]
  );

  const defaultStore = useMemo(() => createTemplateEditorStore(), []);

  return (
    <TemplateEditorStoreContext.Provider value={store ?? defaultStore}>
      <TemplateDataContext.Provider value={contextValue}>
        {children}
      </TemplateDataContext.Provider>
    </TemplateEditorStoreContext.Provider>
  );
};
